import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IAzureSearchRequestOptions, IAzureSearchResponse } from '@vivli/features/search/infrastructure/interface';
import { IAzureSearchApi, IAzureSearchOptionsResult } from '@vivli/core/infrastructure/interface';
import { useRestApi } from '../common';

interface IAzureSearchApiProps {
    options: IAzureSearchOptionsResult;
}

export const useAzureSearchApi = <DocumentType>({ options }: IAzureSearchApiProps): IAzureSearchApi => {
    const { handleGet, ...restApiProps } = useRestApi({ overrideOptions: { params: options.params }, endpoint: options.endpoint });

    const parseUrlParams = ({
        search,
        filter,
        orderBy,
        includeTotalCount,
        facets,
        top,
        skip,
        select
    }: IAzureSearchRequestOptions): URLSearchParams => {
        const result = new URLSearchParams(restApiProps.options.params);

        if (top) {
            result.append('$top', top.toString());
        }

        if (skip) {
            result.append('$skip', skip.toString());
        }

        if (search) {
            result.append('search', search);
        }

        if (filter) {
            result.append('$filter', filter);
        }

        if (orderBy) {
            result.append('$orderby', orderBy);
        }

        if (includeTotalCount) {
            result.append('$count', 'true');
        }

        if (facets) {
            facets.forEach((facet) => {
                result.append('facet', facet);
            });
        }

        if (select) {
            result.append('$select', select);
        }

        return result;
    };

    const getFacets = <FacetType>(options: IAzureSearchRequestOptions): Observable<FacetType> => {
        const params = parseUrlParams(options);

        return handleGet<IAzureSearchResponse<DocumentType, FacetType>>('', { params }).pipe(map((result) => result['@search.facets']));
    };

    const getDocuments = (options: IAzureSearchRequestOptions): Observable<IAzureSearchResponse<DocumentType, any>> => {
        const params = parseUrlParams(options);

        return handleGet<IAzureSearchResponse<DocumentType, any>>('', { params });
    };

    const getDocument = <T>(documentId: string, options: IAzureSearchRequestOptions): Observable<T> => {
        const params = parseUrlParams(options);

        return handleGet<T>(`${documentId}`, { params });
    };

    return {
        getFacets,
        getDocuments,
        getDocument
    };
};
